import React from "react";
import { useTranslation } from "react-i18next";
import classes from "./../../wizard/TaskmapWizard.module.css";
import {
  VictoryAxis,
  VictoryChart,
  VictoryHistogram,
  VictoryTooltip,
} from "victory";
import { VariableRates } from "./VigourTaskmap";

const BIN_COLOR = {
  1: "var(--color-graph-0)",
  2: "var(--color-graph-1)",
  3: "var(--color-graph-2)",
  4: "var(--color-graph-3)",
  5: "var(--color-graph-4)",
  6: "var(--color-graph-5)",
  7: "var(--color-graph-6)",
} as Record<number, string>;

export default function VigourHistogram({
  sortedData,
  rates,
}: {
  sortedData: number[];
  rates: VariableRates;
}): React.ReactNode {
  const { t } = useTranslation();

  const maxValue = sortedData[sortedData.length - 1];
  const bins =
    maxValue > 10
      ? 15
      : [...Array(Math.floor(maxValue) + 1).keys()].map((index) => index + 0.5);

  return (
    <div className={classes.histogram}>
      <VictoryChart
        height={250}
        width={400}
        animate={false}
        padding={{ top: 50, bottom: 50, left: 75, right: 50 }}
      >
        <VictoryHistogram
          style={{
            border: { stroke: "transparent" },
            data: {
              fill: ({ datum }) => {
                const datumValue = datum.x;
                const xRate = rates.get(datumValue);
                const transparency = xRate && xRate > 0 ? 0 : 75;
                return `color-mix(in srgb, ${BIN_COLOR[datumValue]}, transparent ${transparency}%)`;
              },
              strokeWidth: ({ datum }) => {
                const xRate = rates.get(datum.x);
                return xRate && xRate > 0 ? 1 : 0.5;
              },
            },
          }}
          data={sortedData.map((value) => ({ x: value }))}
          bins={bins}
          binSpacing={5}
          labels={({ datum }) =>
            t("tooltip.{{n}} trees", { n: datum.y | 0 }) as string
          }
          labelComponent={
            <VictoryTooltip
              style={{
                fill: "var(--color-green-dark)",
              }}
            />
          }
        />
        <VictoryAxis
          dependentAxis
          fixLabelOverlap={true}
          label={t("taskmap.vigour.histogram.yaxis_description")}
          style={{ axisLabel: { padding: 48 } }}
        />
        <VictoryAxis
          dependentAxis={false}
          fixLabelOverlap={true}
          tickFormat={(value: number) => `${Math.round(value)}`}
          label={t("taskmap.vigour.histogram.xaxis_description")}
          style={{ axisLabel: { padding: 30 } }}
        />
      </VictoryChart>
    </div>
  );
}
